import { allLessons } from 'content-collections'
import { useId, useState } from 'react'
import { Form, Link, useLocation } from 'react-router'
import { FaqAccordion } from '#app/components/brand/faq.tsx'
import { Eyebrow, Heading } from '#app/components/brand/section.tsx'
import { Button } from '#app/components/ui/button.js'
import { Icon } from '#app/components/ui/icon.js'
import {
	groupLessonsByModule,
	totalMinutes,
	visibleSorted,
} from '#app/utils/content.ts'
import { buildMeta, faqPageJsonLd } from '#app/utils/seo.ts'
import { Footer } from '../components/footer'
import Header from '../components/header'
import { type Route } from './+types/index.ts'

const publishedLessons = visibleSorted(allLessons)
const curriculum = groupLessonsByModule(allLessons)
const runtime = totalMinutes(publishedLessons)

const courseUrl = 'https://competitorbacklinks.com/course'
const courseJsonLd = {
	'@context': 'https://schema.org',
	'@type': 'Course',
	name: 'Run competitor backlink research yourself, with AI',
	description:
		'A calm, practical course that teaches you to investigate any competitor’s links using AI and free tools, then turn what you find into links you can earn.',
	provider: {
		'@type': 'Organization',
		name: 'competitorbacklinks.com',
		url: 'https://competitorbacklinks.com',
	},
	url: courseUrl,
	hasCourseInstance: {
		'@type': 'CourseInstance',
		courseMode: ['online', 'self-paced'],
	},
}

export const meta: Route.MetaFunction = () => [
	...buildMeta({
		title: 'The Course: Run competitor backlink research yourself with AI',
		description:
			'A calm, practical course that teaches you to investigate any competitor’s links using AI and free tools, then turn what you find into links you can earn.',
		path: '/course',
		image: '/img/og/course.png',
	}),
	{ 'script:ld+json': courseJsonLd },
	{ 'script:ld+json': faqPageJsonLd(faqs) },
]

const heroPoints = [
	'Lifetime access',
	'30-day refund',
	'No tool subscription needed',
]
const heroStats = [
	{ label: 'Lessons published', value: String(publishedLessons.length) },
	{ label: 'Runtime so far', value: `~${runtime} min` },
	{ label: 'Copy-paste AI prompts', value: 'Included' },
	{ label: 'Templates & checklists', value: 'Included' },
]
const audience = [
	{
		title: 'The DIY marketer',
		text: 'You run your own marketing and want to understand competitors without a four-figure subscription.',
	},
	{
		title: 'The indie founder',
		text: 'You’re technical-ish, curious about AI workflows, and happy to do it yourself if someone shows you how.',
	},
	{
		title: 'The early-career SEO',
		text: 'You’re learning the craft and want a repeatable process you can bring to any team or client.',
	},
]
const outcomes = [
	'Pull any competitor’s full backlink profile in minutes',
	'Use AI to summarise thousands of links into a clear briefing',
	'Tell a reproducible link from noise you should ignore',
	'Build a prioritised list of links you can realistically earn',
	'Write outreach that gets replies without feeling spammy',
	'Run the whole thing as a 20-minute monthly habit',
]
// Our own claims about the course, in our voice. Deliberately NOT testimonials:
// no attribution, no star ratings, no quotation marks. Replace with real
// student quotes once students exist.
const promises = [
	{
		title: 'Built to replace a subscription',
		text: 'The process is designed around free and one-time tools, so you are not locked into a $130 a month suite to do the research.',
	},
	{
		title: 'The AI prompts are included',
		text: 'Every module ships with the exact prompts, so you can run the analysis the same week you learn it instead of reverse-engineering it later.',
	},
	{
		title: 'No hype, just the process',
		text: 'This is the workflow used to run these sites, written down. Where a step does not matter yet, the lesson says so.',
	},
]
const plans = [
	{
		name: 'Self-paced',
		tagline: 'Everything you need, forever',
		price: '$149',
		unit: 'one-time',
		featured: false,
		features: [
			'Every lesson + all AI prompts',
			'Templates & checklists',
			'Lifetime access & updates',
			'30-day refund',
		],
	},
	{
		name: 'Live cohort',
		tagline: 'Learn with a small group + Q&A',
		price: '$349',
		unit: 'per cohort',
		featured: true,
		features: [
			'Everything in Self-paced',
			'4 live sessions with Brad',
			'Feedback on your real research',
			'Private community access',
		],
	},
]
const faqs = [
	{
		q: 'Do I need to pay for an SEO tool to take this?',
		a: 'No. The whole course is built around our free checker and AI. If you later choose a paid tool, we’ll help you pick honestly.',
	},
	{
		q: 'Is this beginner-friendly?',
		a: 'Yes. We define every term and assume no prior SEO knowledge. If you can browse the web, you can do this.',
	},
	{
		q: 'How much AI experience do I need?',
		a: 'None. We give you the exact prompts and walk through each one. You’ll copy, paste, and adapt.',
	},
	{
		q: 'What if it’s not for me?',
		a: 'Email us within 30 days for a full refund, no questions. We’d rather you trust us than keep your money.',
	},
]

function Curriculum() {
	const [open, setOpen] = useState(0)
	const baseId = useId()
	return (
		<div className="flex flex-col gap-3">
			{curriculum.map((group, i) => {
				const isOpen = open === i
				const panelId = `${baseId}-panel-${i}`
				const groupMinutes = totalMinutes(group.lessons)
				return (
					<div
						key={group.module}
						className="border-hairline overflow-hidden rounded-2xl border bg-white"
					>
						<button
							type="button"
							onClick={() => setOpen(isOpen ? -1 : i)}
							aria-expanded={isOpen}
							aria-controls={panelId}
							className="flex w-full items-center gap-4 px-6 py-5 text-left"
						>
							<span className="text-brand w-8 font-mono text-sm font-semibold">
								{String(i + 1).padStart(2, '0')}
							</span>
							<span className="font-display flex-1 text-lg font-semibold">
								{group.module}
							</span>
							<span className="font-mono text-[13px] text-[#9A968C]">
								~{groupMinutes} min
							</span>
							<span
								className={`text-xl text-[#B5B1A8] transition-transform ${isOpen ? 'rotate-45' : ''}`}
								aria-hidden
							>
								+
							</span>
						</button>
						<div
							id={panelId}
							role="region"
							aria-label={group.module}
							hidden={!isOpen}
							className="pr-6 pb-5 pl-[72px]"
						>
							<div className="flex flex-col gap-2.5">
								{group.lessons.map((l) => (
									<Link
										key={l.slug}
										to={`/course/lessons/${l.slug}`}
										className="text-brand flex items-center gap-2.5 text-[14.5px] font-medium"
									>
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											className="shrink-0"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											aria-hidden
										>
											<polygon points="6 4 20 12 6 20 6 4" />
										</svg>
										{l.title}
										<span className="font-mono text-[12px] text-[#9A968C]">
											· {l.duration}
										</span>
									</Link>
								))}
							</div>
						</div>
					</div>
				)
			})}
		</div>
	)
}

export default function CourseLanding() {
	const location = useLocation()
	const [tier, setTier] = useState<string | null>(null)
	const chooseTier = (name: string) => {
		setTier(name)
		document.getElementById('enroll')?.scrollIntoView({ behavior: 'smooth' })
	}
	return (
		<div className="bg-canvas text-ink min-h-screen font-sans">
			<Header />
			<main>
				{/* HERO */}
				<section className="mx-auto grid max-w-[1160px] items-center gap-13 px-7 pt-13 pb-16 lg:grid-cols-[1.05fr_0.95fr]">
					<div>
						<Eyebrow className="mb-5">
							The course · Self-paced + live cohort
						</Eyebrow>
						<Heading
							as="h1"
							className="text-[clamp(36px,5vw,56px)] leading-[1.03] tracking-[-0.03em]"
						>
							Run competitor backlink research yourself, with AI
						</Heading>
						<p className="mt-5 max-w-[520px] text-[19.5px] leading-relaxed text-pretty text-[#57565E]">
							A calm, practical course that teaches you to investigate any
							competitor’s links using AI and free tools, then turn what you
							find into links you can actually earn. No agency, no $129/mo
							subscription required.
						</p>
						<div className="mt-7 flex flex-wrap items-center gap-5">
							<a
								href="#pricing"
								className="bg-brand rounded-xl px-7 py-4 text-base font-semibold text-white hover:opacity-90"
							>
								See the plans
							</a>
							<div className="text-[14.5px] text-[#8A867D]">
								From{' '}
								<span className="font-mono font-semibold text-[#33323A]">
									$149
								</span>{' '}
								· Lifetime access
							</div>
						</div>
						<div className="mt-6 flex flex-wrap items-center gap-5 text-[13.5px] text-[#8A867D]">
							{heroPoints.map((p) => (
								<span key={p} className="flex items-center gap-1.5">
									<Icon name="check" className="text-gained-ink size-3.5" />
									{p}
								</span>
							))}
						</div>
					</div>
					<div className="rounded-[20px] border border-[#E9E4F5] bg-gradient-to-b from-[#FFFEFC] to-[#F6F2FF] p-7 shadow-[0_30px_60px_-34px_rgba(91,75,255,.35)]">
						<div className="mb-4 font-mono text-[11.5px] font-semibold tracking-[0.06em] text-[#9A87D8] uppercase">
							What’s inside
						</div>
						<div className="flex flex-col gap-3.5">
							{heroStats.map((s) => (
								<div
									key={s.label}
									className="flex items-center justify-between border-b border-[#EFEAF7] pb-3.5"
								>
									<span className="text-[15px] text-[#33323A]">{s.label}</span>
									<span className="text-brand font-mono text-base font-semibold">
										{s.value}
									</span>
								</div>
							))}
						</div>
						<p className="mt-4 text-[12.5px] text-[#9A968C]">
							New lessons are being added as the course rolls out.
						</p>
					</div>
				</section>

				{/* WHO IT'S FOR */}
				<section className="bg-ink text-[#F3F1EC]">
					<div className="mx-auto max-w-[960px] px-7 py-[66px]">
						<Eyebrow tone="brand-light" className="mb-4 text-center">
							Who it’s for
						</Eyebrow>
						<Heading className="mx-auto mb-10 text-center text-[clamp(26px,3.6vw,36px)]">
							Built for people who’d rather understand than outsource
						</Heading>
						<div className="grid gap-6 sm:grid-cols-3">
							{audience.map((a) => (
								<div
									key={a.title}
									className="rounded-2xl border border-[#2A2A31] bg-[#1B1B20] p-6"
								>
									<div className="font-display mb-2 text-lg font-semibold text-[#F3F1EC]">
										{a.title}
									</div>
									<p className="text-[14.5px] leading-relaxed text-[#B9B6AE]">
										{a.text}
									</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* OUTCOMES */}
				<section className="mx-auto max-w-[960px] px-7 pt-20">
					<div className="mb-11 text-center">
						<Eyebrow className="mb-3.5">By the end</Eyebrow>
						<Heading className="text-[clamp(26px,4vw,40px)]">
							You’ll be able to do this on your own
						</Heading>
					</div>
					<div className="grid gap-x-10 sm:grid-cols-2">
						{outcomes.map((o) => (
							<div
								key={o}
								className="border-hairline flex items-start gap-3 border-b py-3.5 text-[16.5px] leading-relaxed text-[#2B2A31]"
							>
								<Icon
									name="check"
									className="text-gained-ink mt-1 size-[19px] shrink-0"
								/>
								<span>{o}</span>
							</div>
						))}
					</div>
				</section>

				{/* CURRICULUM */}
				<section className="mx-auto max-w-[820px] px-7 pt-20">
					<div className="mb-10 text-center">
						<Eyebrow className="mb-3.5">Curriculum</Eyebrow>
						<Heading className="mb-2.5 text-[clamp(26px,4vw,40px)]">
							A practical path, lesson by lesson
						</Heading>
						<p className="text-[17px] text-[#57565E]">
							Short lessons you can finish over a weekend, then reuse as a
							reference forever.
						</p>
					</div>
					<Curriculum />
				</section>

				{/* INSTRUCTOR */}
				<section className="mx-auto max-w-[900px] px-7 pt-20">
					<div className="border-hairline grid grid-cols-[auto_1fr] items-center gap-8 rounded-[20px] border bg-white p-9 max-sm:grid-cols-1">
						<div className="from-brand to-brand-light flex size-30 items-end justify-center overflow-hidden rounded-[20px] bg-gradient-to-br">
							<span className="pb-2 font-mono text-[11px] text-white/70">
								founder photo
							</span>
						</div>
						<div>
							<Eyebrow className="mb-2.5">Your instructor</Eyebrow>
							<Heading className="mb-2.5 text-2xl">Brad Williams</Heading>
							<p className="text-base leading-relaxed text-pretty text-[#33323A]">
								I&rsquo;m the founder of DFW AI, and I&rsquo;ve spent two
								decades operating businesses across law, local trades, and
								manufacturing. That was long enough to get tired of watching
								smart people pay four figures a year for backlink data they
								don&rsquo;t know how to read. This course is the exact process I
								use, taught without the jargon, with AI doing the heavy lifting.
							</p>
						</div>
					</div>
				</section>

				{/* WHAT WE PROMISE */}
				<section className="mx-auto max-w-[1080px] px-7 pt-[70px]">
					<div className="mb-6">
						<Eyebrow tone="muted">What we promise</Eyebrow>
					</div>
					<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
						{promises.map((p) => (
							<div
								key={p.title}
								className="border-hairline rounded-2xl border bg-white p-6"
							>
								<div className="font-display text-ink mb-2 text-[17px] font-bold">
									{p.title}
								</div>
								<p className="text-[15px] leading-relaxed text-pretty text-[#57565E]">
									{p.text}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* PRICING */}
				<section id="pricing" className="mx-auto max-w-[900px] px-7 pt-20">
					<div className="mb-10 text-center">
						<Eyebrow className="mb-3.5">Pricing</Eyebrow>
						<Heading className="text-[clamp(26px,4vw,40px)]">
							One fair price. No fake discounts.
						</Heading>
					</div>
					<div className="grid gap-5 sm:grid-cols-2">
						{plans.map((p) => (
							<div
								key={p.name}
								className={`relative rounded-[20px] bg-white p-8 ${p.featured ? 'border-brand border-2' : 'border-hairline border'}`}
							>
								{p.featured ? (
									<span className="bg-brand absolute -top-3 left-8 rounded-full px-3 py-[5px] font-mono text-[11px] font-semibold tracking-[0.05em] text-white uppercase">
										Most popular
									</span>
								) : null}
								<div className="font-display mb-1.5 text-[22px] font-bold">
									{p.name}
								</div>
								<div className="mb-4.5 text-[14.5px] text-[#8A867D]">
									{p.tagline}
								</div>
								<div className="mb-5 flex items-baseline gap-1.5">
									<span className="font-mono text-[40px] font-semibold tracking-[-0.02em]">
										{p.price}
									</span>
									<span className="text-sm text-[#8A867D]">{p.unit}</span>
								</div>
								<div className="mb-6 flex flex-col gap-2.5">
									{p.features.map((f) => (
										<div
											key={f}
											className="flex items-start gap-2.5 text-[14.5px] text-[#33323A]"
										>
											<Icon
												name="check"
												className="text-gained-ink mt-0.5 size-4 shrink-0"
											/>
											<span>{f}</span>
										</div>
									))}
								</div>
								<button
									type="button"
									onClick={() => chooseTier(p.name)}
									className={`block w-full rounded-xl py-3.5 text-center text-[15.5px] font-semibold ${p.featured ? 'bg-brand text-white' : 'border-hairline text-ink border-[1.5px]'}`}
								>
									Get notified: {p.name}
								</button>
							</div>
						))}
					</div>
					<div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#57565E]">
						<Icon name="check-circled" className="text-gained-ink size-4" />
						30-day, no-questions refund. If it doesn’t help, you don’t pay.
					</div>
				</section>

				{/* FAQ */}
				<section className="mx-auto max-w-[760px] px-7 pt-20">
					<Heading className="mb-6 text-center text-[28px]">
						Questions, answered honestly
					</Heading>
					<FaqAccordion faqs={faqs} />
				</section>

				{/* ENROL / WAITLIST */}
				<section
					id="enroll"
					className="mx-auto max-w-[900px] px-7 pt-16 pb-[90px]"
				>
					<div className="bg-ink rounded-[20px] px-8 py-12 text-center text-[#F3F1EC]">
						<Heading className="mb-3.5 text-[clamp(26px,4vw,38px)]">
							Learn it once. Use it forever.
						</Heading>
						<p className="mx-auto mb-2 max-w-[480px] text-[17px] text-[#B9B6AE]">
							Stop renting understanding from a dashboard. Own the skill
							instead.
						</p>
						<p className="mx-auto mb-3 max-w-[500px] text-sm text-[#8A867D]">
							Enrolment opens soon. Join the waitlist and we’ll email you the
							moment it’s live, and you can read the free lessons above right
							now.
						</p>
						{tier ? (
							<p className="text-brand-light mx-auto mb-4 text-sm font-medium">
								Interested in: {tier}
							</p>
						) : null}
						<Form
							method="post"
							action="/resources/subscribe"
							className="mx-auto flex max-w-[440px] flex-col gap-2.5 sm:flex-row"
						>
							<input
								type="hidden"
								name="redirectTo"
								value={location.pathname}
							/>
							<input type="hidden" name="list" value="course" />
							<input type="hidden" name="plan" value={tier ?? ''} />
							<label htmlFor="course-waitlist" className="sr-only">
								Email address for the course waitlist
							</label>
							<input
								id="course-waitlist"
								name="email"
								type="email"
								placeholder="you@company.com"
								className="focus-visible:ring-brand h-12 flex-1 rounded-xl border border-white/15 bg-white/10 px-4 font-mono text-[14.5px] text-white outline-none placeholder:text-white/40 focus-visible:ring-2"
							/>
							<Button type="submit" size="lg" className="h-12 rounded-xl">
								Join the waitlist
							</Button>
						</Form>
					</div>
				</section>
			</main>
			<Footer />
		</div>
	)
}
